'use client';

import { ParsedUrlQuery } from 'querystring';

import { Fragment, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import {
  FacebookOutlined,
  Instagram,
  X,
  YouTube,
  LinkedIn,
  MusicNote,
  Language,
} from '@mui/icons-material';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Divider,
  ListItemButton,
  List,
  Tabs,
  Tab,
} from '@mui/material';
// import { Ads, ClubLogo, Image, useIsSsr } from '@scorenco/components';
import { ClubLogo, Image, Markdown, useIsSsr } from '@scorenco/components';
import {
  ssrApolloService,
  RouteLink,
  usePaginatedQuery,
  useRouteInfo,
  useTranslation,
  Types,
} from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextOptiDigitalAdArea,
  NextOptiDigitalConfig,
  NextPageWithLayout,
} from '@scorenco/next';
import { format } from 'date-fns';
import { useRouter } from 'next/router';

import {
  GroupedEvents,
  JoinClub,
  JsonLd,
  PageLayout,
  PageSeo,
  SportChip,
} from '@/components';
import { useAppRedirect } from '@/hooks/useAppRedirect';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { T_KEYS } from '@/translations';
import { getSport, extractBase36Id } from '@/utils';

import {
  SocialItem,
  WebClubEventsListNextDocument,
  WebClubEventsListPreviousDocument,
  WebClubEventsListTodayDocument,
} from './_internal';
import {
  WebClubEventsListNextQuery,
  WebClubEventsListNextQueryVariables,
  WebClubEventsListPreviousQuery,
  WebClubEventsListPreviousQueryVariables,
  WebClubEventsListTodayQuery,
  WebClubEventsListTodayQueryVariables,
  WebClubInfosDocument,
} from './_internal/Club.query.generated';

const extractUrlInfo = (query: ParsedUrlQuery) => ({
  clubId: extractBase36Id(query.club),
  sportSlug: typeof query.sport === 'string' ? query.sport : undefined,
});

const TabPanel = ({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const MatchsSection = ({ clubId }: { clubId: number }) => {
  const { t } = useTranslation();
  const previousQuery = usePaginatedQuery<
    WebClubEventsListPreviousQuery,
    WebClubEventsListPreviousQueryVariables
  >(WebClubEventsListPreviousDocument, undefined, {
    context: { role: 'anonymous' },
    variables: {
      clubId: Number(clubId),
      today: format(new Date(), 'yyyy-MM-dd'),
      limit: 10,
    },
    skip: !clubId,
  });
  const nextQuery = usePaginatedQuery<
    WebClubEventsListNextQuery,
    WebClubEventsListNextQueryVariables
  >(
    WebClubEventsListNextDocument,
    undefined,
    {
      context: { role: 'anonymous' },
      variables: {
        clubId: Number(clubId),
        today: format(new Date(), 'yyyy-MM-dd'),
        limit: 10,
      },
      skip: !clubId,
    },
    'force-not-today'
  );

  const todayQuery = usePaginatedQuery<
    WebClubEventsListTodayQuery,
    WebClubEventsListTodayQueryVariables
  >(
    WebClubEventsListTodayDocument,
    undefined,
    {
      context: { role: 'anonymous' },
      variables: {
        clubId: Number(clubId),
        today: format(new Date(), 'yyyy-MM-dd'),
        limit: 10,
      },
      skip: !clubId,
    },
    'force-today'
  );

  // Calcule l'onglet par défaut basé sur les données disponibles
  const defaultTab = useMemo(() => {
    return todayQuery?.list && todayQuery.list.length > 0 ? 1 : 0;
  }, [todayQuery?.list]);

  const [currentTab, setCurrentTab] = useState(defaultTab);

  return (
    <Paper variant="bordered" sx={{ p: 0 }}>
      <Box sx={{ width: '100%' }}>
        <Box>
          <Tabs
            value={currentTab}
            onChange={(_event, newValue) => {
              setCurrentTab(newValue);
            }}
            variant="fullWidth"
            textColor="secondary"
            indicatorColor="secondary"
            centered
            css={() => css`
              .MuiButtonBase-root {
                font-size: 0.8rem;
                font-weight: bold;
                padding: 0;
              }
            `}
          >
            <Tab label="Précédent" />
            {(todayQuery?.list?.length ?? 0) > 0 && (
              <Tab
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: '0.5rem',
                        height: '0.5rem',
                        backgroundColor: 'error.main',
                        mr: 1,
                        borderRadius: '100%',
                      }}
                    />
                    {t(T_KEYS.TODAY)}
                  </Box>
                }
              />
            )}

            <Tab label="à venir" />
          </Tabs>
        </Box>
        <TabPanel value={currentTab} index={0}>
          <GroupedEvents
            query={previousQuery}
            showShortName={true}
            showCompetition={true}
          />
        </TabPanel>
        {(todayQuery?.list?.length ?? 0) > 0 && (
          <TabPanel value={currentTab} index={1}>
            <GroupedEvents
              query={todayQuery}
              showShortName={true}
              showCompetition={true}
            />
          </TabPanel>
        )}
        <TabPanel
          value={currentTab}
          index={(todayQuery?.list?.length ?? 0) > 0 ? 2 : 1}
        >
          <GroupedEvents
            query={nextQuery}
            showShortName={true}
            showCompetition={true}
          />
        </TabPanel>
      </Box>
    </Paper>
  );
};

const Club: NextPageWithLayout<{ sport: Types.Clubs_Sport }> = ({ sport }) => {
  const routeInfo = useRouteInfo();
  const router = useRouter();
  const urlInfo = extractUrlInfo(routeInfo.query);
  const isSsr = useIsSsr();
  useAppRedirect();

  useEffect(() => {
    // Si l'URL contient un fragment, on le supprime (pour corriger l'erreur qui
    // rajoutait #google_vignette à l'URL)
    if (window.location.hash) {
      const cleanUrl = window.location.pathname + window.location.search;
      const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      // On ne navigue que si l'URL actuelle est différente de l'URL nettoyée
      if (currentUrl !== cleanUrl) {
        // Utilisation de replace avec shallow: true pour éviter une navigation complète
        router.replace(cleanUrl, undefined, {
          shallow: true,
          scroll: false, // Désactive le scroll automatique
        });
      }
    }
  }, [router]);

  const clubQuery = useQuery(WebClubInfosDocument, {
    variables: { clubId: urlInfo.clubId as number },
    skip: !urlInfo.clubId,
    context: { role: 'anonymous' },
  });

  const club = clubQuery.data?.clubs_get_club?.[0];
  const sportSlug = routeInfo.query.sport;

  return (
    <>
      <JsonLd
        id="jsonld-organization"
        value={{
          '@context': 'http://schema.org',
          '@type': 'Organization',
          image: club?.club?.share_picture && {
            '@type': 'ImageObject',
            url: club.club.share_picture,
            width: '1200',
            height: '630',
          },
          url:
            club?.club?.url &&
            (process.env.NEXT_PUBLIC_WEB_URL as string) + club.club.url,
          name: club?.name,
          address: club?.club?.equipments?.[0]?.place && {
            '@type': 'PostalAddress',
            addressLocality: club.club.equipments[0].place.city?.name,
            addressRegion: 'FR',
            postalCode: club.club.equipments[0].place.postal_code,
            streetAddress: club.club.equipments[0].place.address,
          },
          logo: club?.logo && {
            '@type': 'ImageObject',
            url: club?.logo,
            width: 900,
            height: 900,
          },
        }}
      />

      <PageSeo
        title={`${club?.name} : matches, résultats, classements de votre club de ${sportSlug}`}
        description={`${club?.name} - Suivez les résultats, classements et calendrier des équipes de votre club de ${sportSlug} favoris sur Score'n'co 🔵🟢.`}
        sharedImage={club?.club?.share_picture}
        url={{
          routeKey: RouteKey.Club,
          params: {
            sport: sportSlug || '',
            club: club?.slug + '-' + club?.id.toString(36) || '',
          },
        }}
      />
      <NextOptiDigitalConfig adUnit="/157593316/od.scorenco.com/club" />
      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      <PageLayout
        image={sport?.image}
        breadcrumbs={[
          {
            label: sport?.name || '',
            href: { routeKey: RouteKey.Sport, params: { sport: sportSlug } },
          },
          {
            label: 'Clubs',
            href: { routeKey: RouteKey.Clubs, params: { sport: sportSlug } },
          },
          club?.club?.equipments?.[0]?.place?.city?.department?.code
            ? {
                label:
                  club?.club?.equipments?.[0]?.place?.city?.department?.name ||
                  '',
                href: {
                  routeKey: RouteKey.Clubs,
                  params: {
                    sport: sportSlug,
                    department:
                      club?.club?.equipments?.[0]?.place?.city?.department
                        ?.code,
                  },
                },
              }
            : undefined,
          { label: club?.name || '', current: true },
        ]}
      >
        <Paper variant="bordered">
          <Stack
            direction="row"
            spacing={4}
            width="100%"
            alignItems="center"
            sx={{
              '@media screen and (max-width: 450px)': {
                flexDirection: 'column',
              },
            }}
          >
            <ClubLogo
              logo={club?.logo}
              name={club?.name}
              css={css`
                display: flex;
                width: 8rem;
                height: 8rem;
                @media (max-width: 600px) {
                  width: 6rem;
                  height: 6rem;
                }
                @media (max-width: 450px) {
                  margin-bottom: 1rem;
                }
              `}
              width={128}
              height={128}
            />
            <Box
              sx={{
                '@media screen and (max-width: 450px)': {
                  margin: '0 !important',
                  textAlign: 'center',
                },
              }}
            >
              <SportChip sport={sport?.slug} sx={{ mb: 2 }} />
              <Typography variant="h2" component="h1">
                {clubQuery?.data?.clubs_get_club?.[0].name}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Test pour ces 2 clubs et département du Rhone, voir avec Lucas */}
        {urlInfo.clubId === 124304 || urlInfo.clubId === 122563 ? (
          <iframe
            src={`https://widgets.scorenco.com/auto/sponsors-slider/${urlInfo.clubId}?width=31&height=15.5`}
            style={{
              height: '17.5rem',
              overflow: 'hidden',
              width: '100%',
              border: 'none',
            }}
            scrolling="no"
          ></iframe>
        ) : club?.club?.department?.code === '69' &&
          sportSlug === 'football' ? (
          <a
            href="https://calendly.com/sdsport09"
            target="_blank"
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              flex-direction: row;
              justify-content: center;
            `}
            rel="noreferrer noopener"
          >
            <Image
              src="/images/scorenco_operation.png"
              width="750px"
              style={{
                maxWidth: '100%',
                objectFit: 'contain',
                maxHeight: '187.5px',
              }}
            />
          </a>
        ) : (
          <>
            {/* TODO: Comment this when we have a campaign */}
            <NextAdArea
              akceloSlotId="Scorenco_PlayRVideo"
              akceloAdUnitClass="akcelo-adunit-15786"
              withGooglePublisherTag
            />
            <NextOptiDigitalAdArea areaId="Billboard_1" />
            <NextOptiDigitalAdArea areaId="Mobile_Pos1" />
            {/* {sport?.id && (
              <Ads
                type="club"
                sport_id={sport?.id}
                department_id={club?.club?.department?.id}
                style={{ marginTop: '1rem' }}
                club_name={club?.name}
              />
            )} */}
          </>
        )}

        <Stack
          spacing={2}
          direction="row"
          width="100%"
          sx={{
            '@media screen and (max-width: 700px)': { flexDirection: 'column' },
            mt: 4,
            mb: 4,
          }}
        >
          <Stack
            spacing={3}
            width="100%"
            sx={{ '@media screen and (max-width: 700px)': { mb: 2 } }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{ textTransform: 'uppercase', fontStyle: 'unset', mb: 2 }}
              >
                Équipes
              </Typography>
              <Stack spacing={1} width="100%">
                {/* <Typography variant="h6">
                  Pro
                </Typography> */}
                <Paper
                  variant="bordered"
                  sx={{ padding: 0, alignItems: 'stretch' }}
                >
                  <List disablePadding>
                    {clubQuery?.data?.teams_team_detail_by_club_id?.map(
                      (team, idx) => (
                        <Fragment key={team.team_id || idx}>
                          {idx !== 0 && <Divider />}
                          <ListItemButton
                            component={RouteLink}
                            href={team.url}
                            css={(theme: any) => css`
                              color: ${theme.palette.primary.main};
                              align-items: center;
                              justify-content: start;
                            `}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  textAlign: 'initial',
                                  textTransform: 'initial',
                                  fontWeight: 'bold',
                                }}
                                variant="body1"
                              >
                                {team?.name_in_club}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: 'initial',
                                  textTransform: 'initial',
                                  fontSize: '0.8rem',
                                }}
                              >
                                {team?.competitions?.[0]?.competition?.name}
                              </Typography>
                            </Box>
                          </ListItemButton>
                        </Fragment>
                      )
                    )}
                  </List>
                </Paper>
              </Stack>
            </Box>
            {/* Todo : en attendant que la version connectée soit en V2 */}
            {!isSsr && (
              <Box
                component={RouteLink}
                href={`${process.env.NEXT_PUBLIC_MOBILE_URL}/clubs/${urlInfo.clubId}`}
              >
                <JoinClub />
              </Box>
            )}
          </Stack>

          <Stack
            gap={3}
            width="100%"
            sx={{
              '@media screen and (max-width: 700px)': { m: '0 !important' },
            }}
          >
            <Box>
              <NextOptiDigitalAdArea areaId="Mobile_Pos2" />

              <Typography
                variant="h5"
                sx={{ textTransform: 'uppercase', fontStyle: 'unset', mb: 2 }}
              >
                Matchs {club?.club?.short_name}
              </Typography>
              {urlInfo.clubId && <MatchsSection clubId={urlInfo.clubId} />}
            </Box>

            <Box>
              {(club?.club?.presentation ||
                club?.club?.generated_presentation) && (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      textTransform: 'uppercase',
                      fontStyle: 'unset',
                      mb: 2,
                    }}
                  >
                    Informations
                  </Typography>
                  <Paper
                    variant="bordered"
                    css={css`
                      display: flex;
                      align-items: center;
                      width: 100%;
                      min-height: 8rem;
                      margin-bottom: 2rem;
                      overflow: hidden;
                    `}
                  >
                    <Markdown
                      value={
                        club?.club?.presentation ||
                        club?.club?.generated_presentation
                      }
                    />
                  </Paper>
                </>
              )}
              <Box>
                <Paper
                  variant="bordered"
                  sx={{ padding: 0, alignItems: 'stretch' }}
                >
                  <List disablePadding>
                    {club?.club?.website && (
                      <SocialItem
                        url={club?.club?.website}
                        Icon={Language}
                        name="Site Internet"
                      />
                    )}
                    {club?.club?.facebook_page && (
                      <SocialItem
                        url={club?.club?.facebook_page}
                        name="Facebook"
                        Icon={FacebookOutlined}
                      />
                    )}
                    {club?.club?.instagram_account && (
                      <SocialItem
                        url={club?.club?.instagram_account}
                        name="Instagram"
                        Icon={Instagram}
                      />
                    )}
                    {club?.club?.twitter_account && (
                      <SocialItem
                        url={club?.club?.twitter_account}
                        name="X (Twitter)"
                        Icon={X}
                      />
                    )}
                    {club?.club?.youtube_account && (
                      <SocialItem
                        url={club?.club?.youtube_account}
                        name="YouTube"
                        Icon={YouTube}
                      />
                    )}
                    {club?.club?.linkedin_account && (
                      <SocialItem
                        url={club?.club?.linkedin_account}
                        name="LinkedIn"
                        Icon={LinkedIn}
                      />
                    )}
                    {club?.club?.tiktok_account && (
                      <SocialItem
                        url={club?.club?.tiktok_account}
                        name="TikTok"
                        Icon={MusicNote}
                      />
                    )}
                  </List>
                </Paper>
              </Box>
              {sportSlug === 'basket' && (
                <Box style={{ width: '100%', padding: '0.5rem 1.5rem' }}>
                  <a
                    href="https://scorenco.com/partenaires/ffbb"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Image
                      src="/images/passion_club.png"
                      style={{ cursor: 'pointer', width: '100%' }}
                    />
                  </a>
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>

        <NextOptiDigitalAdArea areaId="Billboard_Bottom" />
        <NextOptiDigitalAdArea areaId="Mobile_Bottom" />
      </PageLayout>
    </>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    if (query['no-ssr'] === undefined) {
      const urlInfo = extractUrlInfo(query);

      if (urlInfo.clubId && urlInfo.sportSlug) {
        const clubQuery = await apolloClient.query({
          query: WebClubInfosDocument,
          variables: { clubId: urlInfo.clubId },
        });

        const club = clubQuery.data?.clubs_get_club?.[0];
        if (club) {
          const sport = await getSport(urlInfo.sportSlug);

          return addApolloState({
            ...previous,
            props: { ...previous.props, sport },
          });
        }
      }

      return { notFound: true };
    }

    return addApolloState(previous);
  },
]);

Club.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Club;
